// Single source of truth for the /blog/visa-sponsorship/:country posts. The
// page heading, meta description, JSON-LD (Article + FAQ), sitemap lastmod and
// the OG image text are all derived from (or tested against) this data, so the
// company count, processing time and dates cannot drift apart between them.

export interface VisaSponsor {
  name: string;
  /** Company page on the board (/companies/:slug). */
  slug?: string;
  /**
   * Board company id to count jobs under when it differs from `slug`. The API
   * cannot filter by "amazon" (it is not a scraped company); AWS jobs are
   * stored as "aws". `null` marks a linked sponsor the API cannot count at all
   * (it rejects ids outside its company list), so it adds no jobs.
   */
  jobsSlug?: string | null;
  /** Careers page, for companies that are not on the board. */
  url?: string;
  description: string;
}

export interface VisaPost {
  country: string;
  title: string;
  /** As used in running text: "Germany", "the Netherlands". */
  countryName: string;
  visaName: string;
  heroImage: string;
  /** YYYY-MM-DD. The post 404s before this date. */
  publishedAt: string;
  /** YYYY-MM-DD. Bump whenever the post's content changes. */
  updatedAt: string;
  processingTime: {
    /** Short figure for the OG image stat. */
    stat: string;
    /** The one processing-time phrase used everywhere on the page. */
    text: string;
  };
  /** First sentences of the meta description; the company count is appended. */
  metaLead: string;
  /** The permit as named in the meta description: "EU Blue Card", "HSM visa". */
  permit: string;
  /** How long it takes, in the meta description: "4–12 weeks", "about 2 weeks". */
  permitTime: string;
  /**
   * Whether the page's own salary range supports "many paying €100k+" in the
   * meta description. Leave false where the article says senior pay is lower.
   */
  over100k: boolean;
  keywords: string;
  /** FAQ schema entries; "{processingTime}" is replaced with processingTime.text. */
  faq: { question: string; answer: string }[];
  companies: VisaSponsor[];
}

const BASE_URL = "https://nextleveljobs.eu";

export const visaPosts: Record<string, VisaPost> = {
  nl: {
    country: "nl",
    title: "Visa Sponsorship in the Netherlands for Software Engineers (2026)",
    countryName: "the Netherlands",
    visaName: "Highly Skilled Migrant visas",
    heroImage: "/og-visa-sponsorship-nl.svg",
    publishedAt: "2026-03-16",
    updatedAt: "2026-03-16",
    processingTime: { stat: "2 weeks", text: "2 weeks" },
    metaLead:
      "The HSM visa takes about 2 weeks to process. Threshold is €5,942/month (age 30+).",
    permit: "HSM visa",
    permitTime: "about 2 weeks",
    over100k: true,
    keywords:
      "netherlands visa sponsorship, highly skilled migrant visa, software engineer netherlands, companies sponsor visa netherlands, tech jobs netherlands",
    faq: [
      {
        question:
          "Why should I consider the Netherlands for a software engineering role?",
        answer:
          "The Netherlands offers a fast visa process (typically 2 weeks), English as the working language at nearly every major tech employer, and the 30% ruling that exempts up to 30% of your salary from income tax for up to 5 years (reducing to 27% from January 2027 for new hires from 2024 onwards). Amsterdam hosts European engineering offices for Booking.com, Adyen, Uber, Databricks, and Optiver. Senior software engineers typically earn between €80,000 and €130,000 in base salary.",
      },
      {
        question:
          "What is the Highly Skilled Migrant (HSM) visa and how does it work?",
        answer:
          "The Highly Skilled Migrant visa (kennismigrantenregeling) is the main work visa for non-EU software engineers moving to the Netherlands. It is employer-sponsored — a company registered as an IND recognised sponsor applies on your behalf. As of 2026, the gross monthly salary threshold is €5,942 for workers aged 30 and over, or €4,357 for those under 30. There is no labour market test and no formal degree requirement. The IND typically decides within two weeks of the employer submitting the application.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in the Netherlands?",
        answer:
          "Apply through the company's careers page and receive a job offer meeting the salary threshold. Your employer — who must be an IND recognised sponsor — submits the HSM visa application on your behalf. You will need your signed employment contract and passport. Once approved, you collect a residence permit card at an IND desk, which also serves as your work permit. The full process from application to approval typically takes two to four weeks.",
      },
    ],
    companies: [
      {
        name: "Booking.com",
        slug: "booking",
        description:
          "One of the world’s largest online travel platforms, headquartered in Amsterdam. Booking.com employs thousands of engineers and is one of the biggest sponsors of HSM visas in the Netherlands. They hire across backend, frontend, data engineering, machine learning, and platform infrastructure. Senior software engineers report total compensation of roughly €115k–€180k according to self-reported data on levels.fyi.",
      },
      {
        name: "Adyen",
        slug: "adyen",
        description:
          "A global payments platform headquartered in Amsterdam, processing transactions for companies including Meta, Uber, and Spotify. Adyen has a highly international engineering team and sponsors visas regularly. Roles span payments engineering, backend systems, infrastructure, and security. Levels.fyi data puts senior engineer total comp in the €110k–€165k range.",
      },
      {
        name: "ASML",
        url: "https://www.asml.com/en/careers",
        description:
          "The world’s sole manufacturer of extreme ultraviolet (EUV) lithography machines used by every leading chipmaker. Headquartered in Veldhoven near Eindhoven, ASML is one of Europe’s most valuable tech companies. They hire software engineers for embedded systems, computational lithography, data infrastructure, and controls software.",
      },
      {
        name: "Uber",
        slug: "uber",
        description:
          "Uber operates a major engineering hub in Amsterdam, focused on its international operations. They sponsor HSM visas and hire for backend, platform, data engineering, and machine learning roles.",
      },
      {
        name: "Google",
        slug: "google",
        description:
          "Google has an engineering office in Amsterdam with teams working on Ads, Cloud, and YouTube products. They sponsor HSM visas for qualified engineers. Roles include backend, infrastructure, SRE, and machine learning.",
      },
      {
        name: "Elastic",
        slug: "elastic",
        description:
          "The company behind Elasticsearch and the Elastic Stack, with a distributed team and offices in Amsterdam. Elastic sponsors visas for engineers working on search, observability, and security products.",
      },
      {
        name: "Databricks",
        slug: "databricks",
        description:
          "A data and AI platform company with a growing Amsterdam office. Databricks sponsors HSM visas and hires for backend engineering, data platform, and ML infrastructure roles. Senior engineers report total comp of €150k–€220k on levels.fyi, making it one of the better-paying options in Amsterdam.",
      },
      {
        name: "Miro",
        slug: "miro",
        description:
          "A visual collaboration platform with its European headquarters in Amsterdam. Miro has a highly international team and sponsors visas. They hire across full-stack, backend, frontend, and platform engineering.",
      },
      {
        name: "NVIDIA",
        slug: "nvidia",
        description:
          "The GPU and AI computing company has offices in the Netherlands, particularly around Eindhoven. They hire for GPU software, driver development, CUDA, and AI infrastructure engineering.",
      },
      {
        name: "Optiver",
        url: "https://optiver.com/working-at-optiver/career-opportunities/",
        description:
          "A global market maker headquartered in Amsterdam, known for some of the highest engineering compensation in Europe. Optiver sponsors HSM visas for software engineers working on low-latency trading systems, infrastructure, and FPGA development. Levels.fyi reports total compensation of €180k–€300k+ for experienced engineers — well above typical Amsterdam tech salaries.",
      },
      {
        name: "Flow Traders",
        url: "https://www.flowtraders.com/careers",
        description:
          "Another Amsterdam-headquartered trading firm that sponsors HSM visas. Flow Traders hires software engineers for trading platform development, low-latency systems, and data engineering.",
      },
      {
        name: "IMC Trading",
        url: "https://www.imc.com/careers/",
        description:
          "A global market-making firm headquartered in Amsterdam. IMC sponsors visas and hires engineers for trading systems, infrastructure, distributed computing, and research engineering.",
      },
      {
        name: "Stripe",
        slug: "stripe",
        description:
          "The payments infrastructure company has an Amsterdam office and sponsors HSM visas. Stripe hires across backend, infrastructure, security, and full-stack engineering.",
      },
      {
        name: "TomTom",
        url: "https://www.tomtom.com/careers/",
        description:
          "A mapping and navigation technology company headquartered in Amsterdam. TomTom sponsors HSM visas and hires for geospatial engineering, backend systems, embedded software, and machine learning.",
      },
      {
        name: "Picnic",
        url: "https://picnic.app/careers/",
        description:
          "A Dutch online grocery delivery company that uses technology to optimise its supply chain. Headquartered in Amsterdam, Picnic sponsors HSM visas and hires for backend, data engineering, machine learning, and mobile development.",
      },
    ],
  },
  de: {
    country: "de",
    title: "Visa Sponsorship in Germany for Software Engineers (2026)",
    countryName: "Germany",
    visaName: "EU Blue Card visas",
    heroImage: "/og-visa-sponsorship-de.svg",
    publishedAt: "2026-03-16",
    updatedAt: "2026-09-28",
    processingTime: {
      stat: "4–12 weeks",
      text: "4–12 weeks within Germany, or 6–20 weeks for embassy applications from abroad",
    },
    metaLead:
      "The EU Blue Card takes 4–12 weeks in Germany (6–20 from abroad). IT roles need €45,934/year.",
    permit: "EU Blue Card",
    permitTime: "4–12 weeks",
    over100k: true,
    keywords:
      "germany visa sponsorship, eu blue card germany, software engineer germany, companies sponsor visa germany, tech jobs germany",
    faq: [
      {
        question:
          "Why should I consider Germany for a software engineering role?",
        answer:
          "Germany has the largest tech sector in Europe, with major engineering hubs in Berlin, Munich, and Hamburg. The EU Blue Card makes relocation straightforward for non-EU engineers — no labour market test is required for IT roles, which are classified as a shortage occupation. Active sponsors include Delivery Hero, Zalando, SAP, and Databricks. Senior software engineers typically earn between €75,000 and €120,000 in base salary.",
      },
      {
        question: "What is the EU Blue Card and how does it work in Germany?",
        answer:
          "The EU Blue Card is an employer-sponsored work and residence permit for highly skilled non-EU workers. In Germany, IT roles are classified as a shortage occupation, so the gross annual salary threshold is €45,934 as of 2026 (€50,700 for other roles). Your employer applies at the local Ausländerbehörde. No labour market test is required for IT roles. Processing typically takes {processingTime}. The Blue Card is valid for up to 4 years and provides a path to permanent residence.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Germany?",
        answer:
          "Secure a job offer from a German employer who confirms EU Blue Card sponsorship. You will need a recognised degree (or equivalent), your signed employment contract, proof of accommodation, and passport. Your employer initiates the application at the local Ausländerbehörde. You may enter Germany on a job-seeker visa or apply from your home country at a German embassy. Processing typically takes {processingTime}.",
      },
    ],
    companies: [
      {
        name: "Delivery Hero",
        slug: "deliveryhero",
        description:
          "A global food delivery platform headquartered in Berlin. Delivery Hero has a highly international engineering team and is one of the most active visa sponsors in Berlin. They hire across backend, mobile, data engineering, ML, and platform infrastructure. Senior engineers report total comp of €85k–€130k on levels.fyi.",
      },
      {
        name: "SAP",
        url: "https://jobs.sap.com/",
        description:
          "Europe’s largest enterprise software company, headquartered in Walldorf near Heidelberg. SAP sponsors Blue Cards at scale and hires for cloud engineering, database internals, AI/ML, enterprise applications, and security. Senior engineers report total comp of €85k–€120k on levels.fyi.",
      },
      {
        name: "Databricks",
        slug: "databricks",
        description:
          "A data and AI platform company with a growing Berlin office. Databricks sponsors Blue Cards and hires for data platform engineering, backend systems, and ML infrastructure.",
      },
      {
        name: "Google",
        slug: "google",
        description:
          "Google has engineering offices in Munich, Berlin, and Hamburg. They sponsor Blue Cards and hire across infrastructure, Cloud, Ads, Android, and machine learning.",
      },
      {
        name: "Meta",
        slug: "meta",
        description:
          "Meta has an engineering office in Berlin and sponsors Blue Cards for qualified engineers. Roles include backend, infrastructure, and Reality Labs engineering.",
      },
      {
        name: "Zalando",
        url: "https://jobs.zalando.com/en/",
        description:
          "Europe’s largest online fashion platform, headquartered in Berlin. Zalando has a large and international engineering organisation and sponsors Blue Cards. Roles cover backend, data engineering, ML, frontend, and logistics technology. Senior engineers report total comp of €90k–€135k on levels.fyi.",
      },
      {
        name: "Personio",
        slug: "personio",
        description:
          "A Munich-based HR software company and one of Germany’s highest-valued startups. Personio sponsors Blue Cards and hires for full-stack, backend, platform, and data engineering.",
      },
      {
        name: "Siemens",
        url: "https://www.siemens.com/global/en/company/jobs.html",
        description:
          "A global industrial technology conglomerate headquartered in Munich. Siemens sponsors Blue Cards and hires software engineers for industrial IoT, cloud platforms, embedded systems, and digital twin technology.",
      },
      {
        name: "Stripe",
        slug: "stripe",
        description:
          "The payments infrastructure company has a Berlin office and sponsors Blue Cards for engineers working on payments systems, infrastructure, and developer tools.",
      },
      {
        name: "N26",
        url: "https://n26.com/en/careers",
        description:
          "A Berlin-based digital bank that sponsors Blue Cards. N26 hires for backend, mobile, security, and data engineering roles.",
      },
      {
        name: "Celonis",
        slug: "celonis",
        description:
          "A Munich-based process mining company and one of Germany’s most valuable enterprise software companies. Celonis sponsors Blue Cards and hires for platform engineering, backend, and data science roles.",
      },
      {
        name: "About You",
        url: "https://www.aboutyou.de/jobs",
        description:
          "A Hamburg-based fashion e-commerce company. About You sponsors Blue Cards and hires for backend, frontend, data engineering, and infrastructure roles.",
      },
      {
        name: "Amazon / AWS",
        slug: "amazon",
        jobsSlug: "aws",
        description:
          "Amazon has engineering teams across Berlin, Munich, and Dresden. They sponsor Blue Cards and hire for software development, cloud infrastructure, and operations engineering.",
      },
      {
        name: "Miro",
        slug: "miro",
        description:
          "The visual collaboration platform has a Berlin office and sponsors Blue Cards. They hire across full-stack, backend, and platform engineering.",
      },
      {
        name: "SoundCloud",
        url: "https://www.soundcloud.com/jobs",
        description:
          "The audio streaming platform headquartered in Berlin. SoundCloud sponsors Blue Cards and hires for backend, audio processing, and infrastructure engineering.",
      },
      {
        name: "Contentful",
        slug: "contentful",
        description:
          "A Berlin-based headless CMS company. Contentful sponsors Blue Cards and hires for backend, platform, and full-stack engineering roles.",
      },
      {
        name: "Trade Republic",
        url: "https://trade-republic.com/careers",
        description:
          "A Berlin-based investment platform and one of Europe’s highest-valued fintech companies. Trade Republic sponsors Blue Cards and hires for backend, mobile, infrastructure, and security engineering.",
      },
    ],
  },
  ie: {
    country: "ie",
    title: "Visa Sponsorship in Ireland for Software Engineers (2026)",
    countryName: "Ireland",
    visaName: "Critical Skills Employment Permits",
    heroImage: "/og-visa-sponsorship-ie.svg",
    publishedAt: "2026-03-16",
    updatedAt: "2026-10-09",
    processingTime: { stat: "4–8 weeks", text: "4–8 weeks" },
    metaLead:
      "The Critical Skills Employment Permit takes 4–8 weeks. Threshold is €40,904/year.",
    permit: "Critical Skills Employment Permit",
    permitTime: "4–8 weeks",
    over100k: true,
    keywords:
      "ireland visa sponsorship, critical skills employment permit, software engineer ireland, companies sponsor visa ireland, tech jobs ireland",
    faq: [
      {
        question:
          "Why should I consider Ireland for a software engineering role?",
        answer:
          "Ireland hosts the European headquarters of Google, Meta, Apple, Stripe, and HubSpot, among others. English is the working language, and the Critical Skills Employment Permit process is well-established. Senior software engineers typically earn between €80,000 and €135,000 in base salary, with total compensation at large tech companies regularly exceeding €150,000 including equity.",
      },
      {
        question:
          "What is the Critical Skills Employment Permit and how does it work?",
        answer:
          "The Critical Skills Employment Permit (CSEP) is Ireland's primary work permit for highly skilled non-EU workers. Software engineering is on the Critical Skills Occupations List, so the salary threshold is €40,904 per year as of 2026. Your employer must be registered in Ireland and submits the application to the Department of Enterprise, Trade and Employment. Processing takes {processingTime}. The permit is granted for two years; after that you can apply for Stamp 4, which removes the requirement for any further employment permit.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Ireland?",
        answer:
          "Secure a job offer of at least two years from an Irish-registered employer. You will need a relevant degree or equivalent experience, your employment contract, and passport. The employer submits the Critical Skills Employment Permit application online to the Department of Enterprise, Trade and Employment. Processing takes {processingTime}. Once approved, you travel to Ireland and register with Irish immigration to receive your residence card.",
      },
    ],
    companies: [
      {
        name: "Google",
        slug: "google",
        description:
          "Google employs thousands of engineers in Dublin, with teams working on core products including Ads, Cloud, and Search. They sponsor Critical Skills Employment Permits and hire across backend, infrastructure, SRE, and machine learning. Senior engineers report total comp of €130k–€190k on levels.fyi.",
      },
      {
        name: "Stripe",
        slug: "stripe",
        description:
          "Stripe was co-founded by Irish brothers and has a major Dublin engineering hub. They sponsor employment permits and hire for payments infrastructure, backend, security, and developer platform engineering. Senior engineers report total comp of €130k–€175k on levels.fyi.",
      },
      {
        name: "HubSpot",
        slug: "hubspot",
        description:
          "HubSpot has its European headquarters and a significant engineering team in Dublin. They sponsor permits and hire for backend, frontend, platform, and data engineering roles.",
      },
      {
        name: "Meta",
        slug: "meta",
        description:
          "Meta operates a European engineering hub in Dublin’s Docklands. They sponsor employment permits for engineers working on infrastructure, privacy, integrity, and product engineering. Senior engineers report total comp of €145k–€200k on levels.fyi.",
      },
      {
        name: "Apple",
        slug: "apple",
        description:
          "Apple has operations in Cork and Dublin, including engineering teams working on Maps, Siri, and services infrastructure. They sponsor employment permits for qualified engineers.",
      },
      {
        name: "Intercom",
        url: "https://www.intercom.com/careers",
        description:
          "An Irish-founded customer messaging platform with engineering based in Dublin. Intercom sponsors permits and hires for backend, full-stack, ML, and platform engineering.",
      },
      {
        name: "MongoDB",
        slug: "mongo",
        description:
          "The database company has a Dublin engineering office. They sponsor employment permits and hire for database internals, cloud platform, distributed systems, and developer tools engineering.",
      },
      {
        name: "Workday",
        url: "https://www.workday.com/en-us/company/careers/overview.html",
        description:
          "Workday has its European headquarters in Dublin with a large engineering team. They sponsor permits and hire for backend, cloud platform, data engineering, and ML roles.",
      },
      {
        name: "Salesforce",
        url: "https://www.salesforce.com/company/careers/",
        description:
          "Salesforce has a significant Dublin presence with engineering teams. They sponsor employment permits and hire for platform, backend, and cloud infrastructure engineering.",
      },
      {
        name: "Amazon / AWS",
        slug: "amazon",
        jobsSlug: "aws",
        description:
          "Amazon has operations in Dublin with engineering teams working on AWS services and retail technology. They sponsor employment permits for software development, cloud infrastructure, and operations engineering.",
      },
      {
        name: "Indeed",
        url: "https://www.indeed.com/cmp/Indeed/about",
        description:
          "The job search company has its international headquarters in Dublin with a substantial engineering team. They sponsor permits and hire for search, backend, data, and ML engineering.",
      },
      {
        name: "Microsoft",
        slug: "microsoft",
        description:
          "Microsoft has its European operations centre in Dublin with engineering teams across Azure, Office 365, and LinkedIn. They sponsor employment permits for software engineering and cloud infrastructure roles.",
      },
      {
        name: "Twilio",
        slug: "twilio",
        description:
          "The communications platform has a Dublin engineering office. Twilio sponsors employment permits and hires for backend, communications infrastructure, and API development.",
      },
      {
        name: "Zendesk",
        url: "https://www.zendesk.com/jobs/",
        description:
          "Zendesk has a Dublin engineering office and sponsors employment permits. They hire for backend, frontend, data, and platform engineering.",
      },
      {
        name: "Poppulo",
        url: "https://www.poppulo.com/careers",
        description:
          "An Irish-founded enterprise communications company headquartered in Cork. Poppulo sponsors employment permits and hires for full-stack, backend, and cloud engineering.",
      },
      {
        name: "Squarespace",
        slug: "squarespace",
        description:
          "The website building platform has a Dublin engineering office. Squarespace sponsors employment permits and hires for backend, frontend, and infrastructure engineering.",
      },
    ],
  },
  fr: {
    country: "fr",
    title: "Visa Sponsorship in France for Software Engineers (2026)",
    countryName: "France",
    visaName: "Talent Passport visas",
    heroImage: "/og-visa-sponsorship-fr.svg",
    publishedAt: "2026-04-01",
    updatedAt: "2026-10-09",
    processingTime: { stat: "2–4 weeks", text: "2–4 weeks" },
    metaLead: "The Talent Passport takes 2–4 weeks. Threshold is €38,616/year.",
    permit: "Talent Passport",
    permitTime: "2–4 weeks",
    over100k: false,
    keywords:
      "france visa sponsorship, talent passport france, passeport talent, software engineer france, companies sponsor visa france, tech jobs france",
    faq: [
      {
        question:
          "Why should I consider France for a software engineering role?",
        answer:
          "France has a thriving tech scene centred on Paris, with major companies like Datadog, Criteo, and BlaBlaCar headquartered there. The Talent Passport (Passeport Talent) visa offers a 4-year permit with no separate work permit required, and the salary threshold of €38,616/year is one of the lowest in Western Europe. Senior software engineers typically earn between €60,000 and €100,000 in base salary.",
      },
      {
        question: "What is the Talent Passport visa and how does it work?",
        answer:
          "The Talent Passport (Passeport Talent) is France's primary visa for highly skilled workers. It is a combined residence and work permit valid for up to 4 years and renewable. For salaried employees, the gross annual salary must be at least 1.5 times the French minimum wage (approximately €38,616/year as of 2026). No separate work permit is required. France also offers the EU Blue Card as an alternative route.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in France?",
        answer:
          "Secure a job offer from a French employer meeting the salary threshold. Your employer registers the contract with the French authorities. You then apply for the Talent Passport at the French consulate in your country. Processing typically takes {processingTime} for the visa, after which you travel to France and obtain your residence permit card from the local prefecture.",
      },
    ],
    companies: [
      {
        name: "Datadog",
        slug: "datadog",
        description:
          "A cloud monitoring and analytics platform with a significant Paris engineering office. Datadog was co-founded in France and sponsors Talent Passport visas. They hire across backend, infrastructure, observability, and security engineering.",
      },
      {
        name: "Criteo",
        url: "https://www.criteo.com/careers/",
        description:
          "A Paris-headquartered ad-tech company specialising in commerce media. Criteo sponsors Talent Passport visas and hires for backend, ML, data engineering, and platform infrastructure roles.",
      },
      {
        name: "Stripe",
        slug: "stripe",
        description:
          "The payments infrastructure company has a Paris office and sponsors Talent Passport visas. Stripe hires across backend, infrastructure, security, and full-stack engineering.",
      },
      {
        name: "Alan",
        slug: "alan",
        jobsSlug: null,
        description:
          "A French health insurance company built on technology. Alan sponsors Talent Passport visas and hires for full-stack, backend, and data engineering roles. They are one of France's most-valued startups.",
      },
      {
        name: "BlaBlaCar",
        url: "https://blog.blablacar.com/careers",
        description:
          "A Paris-headquartered carpooling and bus platform. BlaBlaCar sponsors Talent Passport visas and hires for backend, mobile, data engineering, and platform infrastructure.",
      },
      {
        name: "Doctolib",
        url: "https://careers.doctolib.com/",
        description:
          "Europe's leading healthcare booking platform, headquartered in Paris. Doctolib sponsors Talent Passport visas and hires for backend, frontend, data, and infrastructure engineering.",
      },
    ],
  },
  pl: {
    country: "pl",
    title: "Visa Sponsorship in Poland for Software Engineers (2026)",
    countryName: "Poland",
    visaName: "work permits and EU Blue Cards",
    heroImage: "/og-visa-sponsorship-pl.svg",
    publishedAt: "2026-04-06",
    updatedAt: "2026-10-09",
    processingTime: { stat: "1–3 months", text: "1–3 months" },
    metaLead:
      "The single work permit takes 1–3 months (delays common). EU Blue Card needs ~€2,000+/month.",
    permit: "single work permit",
    permitTime: "1–3 months",
    over100k: true,
    keywords:
      "poland visa sponsorship, work permit poland, eu blue card poland, software engineer poland, companies sponsor visa poland, tech jobs poland",
    faq: [
      {
        question:
          "Why should I consider Poland for a software engineering role?",
        answer:
          "Poland has a rapidly growing tech sector with major hubs in Warsaw, Krakow, and Wroclaw. Cost of living is significantly lower than Western Europe while salaries at international companies remain competitive. Google, Snowflake, and Databricks all have engineering offices in Poland. Senior software engineers typically earn between PLN 25,000 and PLN 40,000 per month gross.",
      },
      {
        question:
          "What is the Polish work permit process and how does it work?",
        answer:
          "Poland offers a combined temporary residence and work permit (single permit) that allows non-EU workers to live and work in Poland. The employer applies on behalf of the worker at the local voivodeship office. Processing typically takes {processingTime} but delays are common. Poland also offers the EU Blue Card for highly qualified workers. No specific salary threshold applies to the standard work permit, but the Blue Card requires a salary of at least 1.5 times the national average.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Poland?",
        answer:
          "Secure a job offer from a Polish employer. Your employer initiates the work permit application at the local voivodeship office. You then apply for a work visa at the Polish consulate. Processing takes {processingTime}. Once in Poland, you apply for the temporary residence and work permit card at the voivodeship office.",
      },
    ],
    companies: [
      {
        name: "Google",
        slug: "google",
        description:
          "Google has an engineering office in Warsaw with teams working on Cloud, infrastructure, and core products. They sponsor work permits and hire across backend, SRE, and machine learning engineering.",
      },
      {
        name: "Snowflake",
        slug: "snowflake",
        description:
          "The cloud data platform company has an engineering office in Warsaw. Snowflake sponsors work permits and hires for backend, database internals, and cloud infrastructure engineering.",
      },
      {
        name: "Databricks",
        slug: "databricks",
        description:
          "The data and AI platform company has a growing presence in Poland. Databricks sponsors work permits and hires for data platform, backend, and ML infrastructure roles.",
      },
      {
        name: "Revolut",
        slug: "revolut",
        description:
          "The fintech company has an engineering hub in Poland. Revolut sponsors work permits and hires for backend, mobile, payments, and platform engineering.",
      },
      {
        name: "Microsoft",
        slug: "microsoft",
        description:
          "Microsoft has operations in Poland with engineering teams. They sponsor work permits and hire for software development, cloud infrastructure, and product engineering roles.",
      },
    ],
  },
  se: {
    country: "se",
    title: "Visa Sponsorship in Sweden for Software Engineers (2026)",
    countryName: "Sweden",
    visaName: "work permits",
    heroImage: "/og-visa-sponsorship-se.svg",
    publishedAt: "2026-04-10",
    updatedAt: "2026-09-28",
    processingTime: { stat: "1–3 months", text: "1–3 months" },
    metaLead:
      "The Migrationsverket work permit takes 1–3 months. Minimum SEK 35,000/month for tech.",
    permit: "Swedish work permit",
    permitTime: "1–3 months",
    over100k: false,
    keywords:
      "sweden visa sponsorship, work permit sweden, migrationsverket, software engineer sweden, companies sponsor visa sweden, tech jobs sweden",
    faq: [
      {
        question:
          "Why should I consider Sweden for a software engineering role?",
        answer:
          "Sweden is home to some of Europe's most successful tech companies, including Spotify, Klarna, and King. Stockholm has produced more billion-dollar startups per capita than almost any city outside Silicon Valley. The work permit process is employer-driven with no points system. English is widely spoken in Swedish workplaces. Senior software engineers typically earn between SEK 55,000 and SEK 75,000 per month.",
      },
      {
        question: "What is the Swedish work permit and how does it work?",
        answer:
          "Sweden's work permit is employer-sponsored and managed by Migrationsverket (the Swedish Migration Agency). There is no points system. The employer must offer terms consistent with Swedish collective agreements or sector standards. For tech roles, the salary must exceed 80% of the sector median — approximately SEK 35,000 per month or more. Processing typically takes {processingTime}. The permit is tied to the employer for the first two years.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Sweden?",
        answer:
          "Secure a job offer from a Swedish employer. The employer advertises the position and confirms the terms meet collective agreement standards. You apply for a work permit through Migrationsverket online. Processing takes {processingTime}. Once approved, you receive a residence permit card and can travel to Sweden to begin work.",
      },
    ],
    companies: [
      {
        name: "Spotify",
        slug: "spotify",
        description:
          "The world's largest audio streaming platform, headquartered in Stockholm. Spotify sponsors work permits and hires across backend, infrastructure, ML, data engineering, and mobile development. They have one of the most international engineering teams in Sweden.",
      },
      {
        name: "Klarna",
        slug: "klarna",
        jobsSlug: null,
        description:
          "A Stockholm-based fintech company offering buy-now-pay-later services. Klarna sponsors work permits and hires for backend, payments, platform, and data engineering roles.",
      },
      {
        name: "King",
        slug: "king",
        jobsSlug: null,
        description:
          "The game studio behind Candy Crush, headquartered in Stockholm. King sponsors work permits and hires for game engine, backend, data, and platform engineering roles.",
      },
      {
        name: "Mojang",
        url: "https://www.mojang.com/careers",
        description:
          "The studio behind Minecraft, based in Stockholm and owned by Microsoft. Mojang sponsors work permits and hires for game development, backend, and platform engineering.",
      },
      {
        name: "Einride",
        url: "https://www.einride.tech/careers/",
        description:
          "A Swedish autonomous freight company building electric and self-driving trucks. Einride sponsors work permits and hires for autonomy, embedded systems, backend, and infrastructure engineering.",
      },
    ],
  },
  pt: {
    country: "pt",
    title: "Visa Sponsorship in Portugal for Software Engineers (2026)",
    countryName: "Portugal",
    visaName: "Tech Visas and D3 visas",
    heroImage: "/og-visa-sponsorship-pt.svg",
    publishedAt: "2026-04-17",
    updatedAt: "2026-10-09",
    processingTime: { stat: "1–2 months", text: "1–2 months" },
    metaLead:
      "The D3 visa takes 1–2 months, fast-tracked by the Tech Visa. NHR tax ended in 2024.",
    permit: "D3 visa",
    permitTime: "1–2 months",
    over100k: false,
    keywords:
      "portugal visa sponsorship, tech visa portugal, d3 visa portugal, software engineer portugal, companies sponsor visa portugal, tech jobs portugal",
    faq: [
      {
        question:
          "Why should I consider Portugal for a software engineering role?",
        answer:
          "Portugal has a growing tech scene centred on Lisbon and Porto, with a warm climate, affordable cost of living compared to Western Europe, and an increasingly international workforce. Companies like Farfetch, Google, and Cloudflare have engineering offices there. The Tech Visa programme simplifies sponsorship for certified companies. Note that the NHR (Non-Habitual Resident) tax regime ended for new applicants from 2024. Senior software engineers typically earn between €45,000 and €75,000 in base salary.",
      },
      {
        question: "What is the Portugal Tech Visa and how does it work?",
        answer:
          "The Tech Visa (Visto Tech) is a fast-track certification programme for companies, allowing them to sponsor non-EU workers more easily. Workers hired by certified companies can apply for a D3 Highly Qualified Activity visa. The D3 visa requires a job offer for a highly qualified role and typically a relevant degree or equivalent experience. Processing is faster than standard work visas — typically {processingTime}.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Portugal?",
        answer:
          "Secure a job offer from a Portuguese employer, ideally one certified under the Tech Visa programme. Apply for a D3 visa at the Portuguese consulate with your employment contract and qualifications. Processing takes {processingTime}. Once in Portugal, register with SEF (immigration services) and obtain your residence permit.",
      },
    ],
    companies: [
      {
        name: "Farfetch",
        url: "https://www.farfetch.com/careers",
        description:
          "A luxury fashion e-commerce platform with engineering hubs in Lisbon and Porto. Farfetch sponsors D3 visas and hires for backend, frontend, data engineering, and platform infrastructure.",
      },
      {
        name: "Google",
        slug: "google",
        description:
          "Google has an engineering presence in Lisbon. They sponsor work permits and hire across backend, Cloud, and infrastructure engineering.",
      },
      {
        name: "Cloudflare",
        slug: "cloudflare",
        description:
          "The web infrastructure and security company has an engineering office in Lisbon. Cloudflare sponsors D3 visas and hires for systems, networking, security, and backend engineering.",
      },
      {
        name: "OutSystems",
        url: "https://www.outsystems.com/careers/",
        description:
          "A Portuguese-founded low-code platform company headquartered in Lisbon. OutSystems sponsors D3 visas and hires for platform, backend, and full-stack engineering roles.",
      },
      {
        name: "Feedzai",
        url: "https://www.feedzai.com/careers/",
        description:
          "A Portuguese AI company specialising in fraud detection for financial institutions. Feedzai sponsors D3 visas and hires for ML, data engineering, backend, and platform roles.",
      },
    ],
  },
  gb: {
    country: "gb",
    title: "Visa Sponsorship in the UK for Software Engineers (2026)",
    countryName: "the UK",
    visaName: "Skilled Worker visas",
    heroImage: "/og-visa-sponsorship-gb.svg",
    publishedAt: "2026-10-15",
    updatedAt: "2026-10-15",
    processingTime: {
      stat: "3–8 weeks",
      text: "3–8 weeks (about 3 weeks when you apply from outside the UK, up to 8 weeks from inside)",
    },
    metaLead:
      "The Skilled Worker visa takes 3–8 weeks. Threshold is £41,700/year or the role's going rate.",
    permit: "Skilled Worker visa",
    permitTime: "3–8 weeks",
    over100k: true,
    keywords:
      "uk visa sponsorship, skilled worker visa, software engineer uk, companies sponsor visa uk, tech jobs london, sponsor licence",
    faq: [
      {
        question:
          "Why should I consider the UK for a software engineering role?",
        answer:
          "London has the highest posted software-engineering pay in Europe: senior listings from the employers on this board have a median base salary of about €119,000, and the AI labs (Anthropic, OpenAI) and trading firms (Jane Street, Hudson River Trading, G-Research) post far more. Everything runs in English, the Skilled Worker visa is employer-sponsored with no labour-market test, and the big employers in London have in-house immigration teams. The trade-off is cost: London rent and the visa fees (the Immigration Health Surcharge is paid up front for the whole visa) are the highest on this list.",
      },
      {
        question: "What is the Skilled Worker visa and how does it work?",
        answer:
          "The Skilled Worker visa is the UK's main employer-sponsored work visa. Your employer must hold a Home Office sponsor licence and issues you a Certificate of Sponsorship for a specific job. Since 22 July 2025 the job must be at degree level (RQF 6), which software engineering roles are, and the salary must be at least £41,700 a year or the going rate for the occupation, whichever is higher; a €100k offer clears both. You also need English at B1 level. Processing takes {processingTime}. The visa can be granted for up to five years and extended, and currently leads to settlement (indefinite leave to remain) after five years, though the government has proposed lengthening that, so check the rules in force when you apply.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in the UK?",
        answer:
          "Get an offer from an employer on the Home Office register of licensed sponsors; the register is public, so check a company against it before you apply. The employer assigns you a Certificate of Sponsorship, which carries the job title, salary and start date. You then apply online with the certificate reference, your passport, proof of English and the fees, including the Immigration Health Surcharge. Processing takes {processingTime}. Your partner and children can apply as dependants at the same time.",
      },
    ],
    companies: [
      {
        name: "Google / DeepMind",
        slug: "google",
        description:
          "Google's London offices, including DeepMind at King's Cross, are among its largest engineering sites outside the US. They sponsor Skilled Worker visas and hire across ML research, infrastructure, Cloud and product engineering.",
      },
      {
        name: "Anthropic",
        slug: "anthropic",
        description:
          "Anthropic's London office is its first outside the US. They sponsor Skilled Worker visas and hire for research engineering, infrastructure and product. The four senior listings with posted pay on this board have a median base salary of about €378,000, the highest of any UK employer here.",
      },
      {
        name: "OpenAI",
        slug: "openai",
        description:
          "OpenAI's London office hires research and applied engineers and sponsors Skilled Worker visas. Senior listings here post a median base salary of about €280,000.",
      },
      {
        name: "Meta",
        slug: "meta",
        description:
          "Meta's London engineering hub is its largest in Europe, with teams on WhatsApp, Instagram, infrastructure and AI. They sponsor Skilled Worker visas and run a dedicated immigration team.",
      },
      {
        name: "Amazon / AWS",
        slug: "amazon",
        jobsSlug: "aws",
        description:
          "Amazon and AWS have large engineering teams in London, Cambridge and Edinburgh. They sponsor Skilled Worker visas and hire for AWS services, Alexa, retail systems and applied science.",
      },
      {
        name: "Microsoft",
        slug: "microsoft",
        description:
          "Microsoft's UK engineering is spread across London, Cambridge (Microsoft Research) and Reading. They sponsor Skilled Worker visas and hire for Azure, AI and research engineering.",
      },
      {
        name: "Jane Street",
        slug: "janestreet",
        description:
          "The trading firm's London office is its largest outside New York. Jane Street sponsors Skilled Worker visas and hires software engineers, often OCaml, with total compensation that starts well into six figures from the first year.",
      },
      {
        name: "Hudson River Trading",
        slug: "hrt",
        description:
          "HRT's London office builds trading systems and research infrastructure. They sponsor Skilled Worker visas and hire C++ and Python engineers at pay comparable to the other top trading firms.",
      },
      {
        name: "G-Research",
        slug: "gresearch",
        description:
          "A London-based quantitative research firm that sponsors Skilled Worker visas and hires software engineers, platform engineers and researchers; one of the larger engineering employers in the City.",
      },
      {
        name: "Monzo",
        slug: "monzo",
        description:
          "The UK digital bank sponsors Skilled Worker visas and is one of the most prolific listers on this board: 17 senior listings with posted pay, median base salary about €133,000. Backend (Go), platform, data and security engineering.",
      },
      {
        name: "Wise",
        slug: "wise",
        description:
          "Wise is the largest single lister in the UK on this board and sponsors Skilled Worker visas. Engineering is in London and Tallinn, across payments infrastructure, platform and product.",
      },
      {
        name: "Revolut",
        slug: "revolut",
        description:
          "Revolut's engineering is headquartered in London and it sponsors Skilled Worker visas. Backend (Java/Kotlin), data, ML and security roles, with a fast-moving interview process.",
      },
      {
        name: "Deliveroo",
        slug: "deliveroo",
        description:
          "Deliveroo's engineering is based in London and it sponsors Skilled Worker visas. Backend, platform, data and ML roles for logistics and consumer systems.",
      },
      {
        name: "Cloudflare",
        slug: "cloudflare",
        description:
          "Cloudflare's London office is its EMEA engineering hub. They sponsor Skilled Worker visas and hire for systems, network, security and product engineering, with many roles also open remote across the UK.",
      },
      {
        name: "Stripe",
        slug: "stripe",
        description:
          "Stripe has a London engineering office alongside its Dublin hub and sponsors Skilled Worker visas. Payments infrastructure, backend and platform roles.",
      },
      {
        name: "Palantir",
        slug: "palantir",
        description:
          "Palantir's London office is its largest outside the US. They sponsor Skilled Worker visas and hire forward-deployed and platform software engineers.",
      },
    ],
  },
  ch: {
    country: "ch",
    title: "Visa Sponsorship in Switzerland for Software Engineers (2026)",
    countryName: "Switzerland",
    visaName: "B permits",
    heroImage: "/og-visa-sponsorship-ch.svg",
    publishedAt: "2026-04-24",
    updatedAt: "2026-09-28",
    processingTime: { stat: "1–3 months", text: "1–3 months" },
    metaLead:
      "The B permit takes 1–3 months and is quota-based. Hardest process in Europe.",
    permit: "B permit",
    permitTime: "1–3 months",
    over100k: true,
    keywords:
      "switzerland visa sponsorship, b permit switzerland, software engineer switzerland, companies sponsor visa switzerland, tech jobs switzerland",
    faq: [
      {
        question:
          "Why should I consider Switzerland for a software engineering role?",
        answer:
          "Switzerland offers some of the highest software engineering salaries in Europe, with senior engineers earning CHF 120,000 to CHF 180,000 or more. Google has a major engineering hub in Zurich, and UBS, Microsoft, and Apple all have significant Swiss operations. The quality of life is exceptionally high. However, the visa process is the most restrictive on this list — Switzerland uses a quota-based system for non-EU/EFTA workers.",
      },
      {
        question: "What is the Swiss B permit and how does it work?",
        answer:
          "The B permit is a residence and work permit for employed foreign nationals in Switzerland. For non-EU/EFTA citizens, it is quota-based — the Swiss government sets annual limits on the number of permits issued. Employers must prove that no suitable candidate from Switzerland or the EU/EFTA could be found for the role. The employer applies through the cantonal labour market authority. Processing typically takes {processingTime}. The B permit is initially valid for one year and renewable.",
      },
      {
        question:
          "How do I apply for a visa-sponsored software engineering job in Switzerland?",
        answer:
          "Secure a job offer from a Swiss employer willing to sponsor your B permit. The employer must demonstrate to the cantonal authorities that no suitable EU/EFTA candidate is available. Once approved, you apply for a D visa at the Swiss consulate. After arriving in Switzerland, you register with the cantonal migration office and receive your B permit. Processing typically takes {processingTime} in total.",
      },
    ],
    companies: [
      {
        name: "Google",
        slug: "google",
        description:
          "Google has one of its largest engineering offices outside the US in Zurich, with thousands of engineers working on Search, YouTube, Maps, Cloud, and core infrastructure. They sponsor B permits and are one of the most active visa sponsors in Switzerland. Senior engineers report total comp of CHF 180k–300k+ on levels.fyi.",
      },
      {
        name: "UBS",
        url: "https://www.ubs.com/global/en/careers.html",
        description:
          "Switzerland's largest bank has a major technology division in Zurich. UBS sponsors B permits and hires for backend, trading systems, cloud infrastructure, data engineering, and security roles.",
      },
      {
        name: "Microsoft",
        slug: "microsoft",
        description:
          "Microsoft has engineering operations in Zurich. They sponsor B permits and hire for cloud platform, AI, and product engineering roles.",
      },
      {
        name: "Apple",
        slug: "apple",
        description:
          "Apple has an engineering presence in Zurich. They sponsor B permits and hire for ML, Siri, and services engineering.",
      },
      {
        name: "Goldman Sachs",
        url: "https://www.goldmansachs.com/careers/",
        description:
          "Goldman Sachs has a technology office in Zurich. They sponsor B permits and hire for trading platform engineering, infrastructure, and data engineering.",
      },
    ],
  },
};

export const visaPostUrl = (post: VisaPost) =>
  `${BASE_URL}/blog/visa-sponsorship/${post.country}`;

export const visaCompaniesHeading = (post: VisaPost) =>
  `${post.companies.length} Companies That Sponsor Visas in ${post.countryName}`;

export function visaMetaDescription(post: VisaPost): string {
  const [first, second] = post.companies;
  return `${post.metaLead} ${post.companies.length} companies including ${first.name} and ${second.name} are sponsoring in 2026.`;
}

/**
 * Which headline format a page gets, from its open-job count N (jobs in the
 * country at the sponsors that are on the board):
 * - "jobs": N >= JOBS_FORMAT_MIN_JOBS, the title leads with the job count.
 * - "companies": MIN_LIVE_JOBS <= N < JOBS_FORMAT_MIN_JOBS, it leads with the
 *   company count instead of a small job count.
 * - "evergreen": N is missing or below MIN_LIVE_JOBS, the old title stays.
 */
export type VisaTier = "jobs" | "companies" | "evergreen";

export const JOBS_FORMAT_MIN_JOBS = 20;
export const MIN_LIVE_JOBS = 5;

/** Titles longer than this are shortened (they get truncated in results). */
const MAX_TITLE_LENGTH = 65;

export function visaTier(openJobs: number | undefined): VisaTier {
  if (openJobs === undefined || !Number.isFinite(openJobs)) return "evergreen";
  if (openJobs >= JOBS_FORMAT_MIN_JOBS) return "jobs";
  if (openJobs >= MIN_LIVE_JOBS) return "companies";
  return "evergreen";
}

export interface VisaPageView {
  tier: VisaTier;
  /** True when a count-led title is in use (tiers "jobs" and "companies"). */
  live: boolean;
  title: string;
  h1: string;
  description: string;
  /** YYYY-MM-DD; bumped to the first of the month while the live wording is on. */
  updatedAt: string;
  /** The count the live wording is built on; only set when `live`. */
  openJobs?: number;
}

/**
 * Board company ids whose jobs count for the post: its sponsors with a board
 * page, under `jobsSlug` where that differs and none for `jobsSlug: null`.
 */
export const visaSponsorSlugs = (post: VisaPost): string[] => [
  ...new Set(
    post.companies.flatMap((company) => {
      const slug =
        company.jobsSlug === undefined ? company.slug : company.jobsSlug;
      return slug ? [slug] : [];
    }),
  ),
];

const monthYear = (now: Date) =>
  now.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const capitalise = (text: string) => text[0].toUpperCase() + text.slice(1);

function liveTitle(post: VisaPost, tier: VisaTier, n: string, c: string) {
  const country = post.countryName.replace(/^the /, "");
  if (tier === "jobs") {
    return `${country} Visa Sponsorship: ${n} Software Jobs at ${c} Companies`;
  }
  const long = `${country} Visa Sponsorship for Software Engineers: ${c} Companies Hiring`;
  return long.length <= MAX_TITLE_LENGTH
    ? long
    : `${country} Visa Sponsorship: ${c} Companies Hiring Engineers`;
}

function liveDescription(post: VisaPost, tier: VisaTier, n: string, c: string) {
  const pay = post.over100k ? ", many paying €100k+" : "";
  const lead =
    tier === "jobs"
      ? `${n} open roles at ${c} companies that sponsor the ${post.permit}`
      : `${c} companies that sponsor the ${post.permit} have ${n} open roles`;
  return `${lead}${pay}. ${capitalise(post.permit)} takes ${post.permitTime}.`;
}

/**
 * What the page shows for its title, H1, description and last-modified date,
 * chosen from the open-job count (see VisaTier). Without a usable count the
 * page keeps its evergreen wording.
 */
export function visaPageView(
  post: VisaPost,
  openJobs: number | undefined,
  now: Date = new Date(),
): VisaPageView {
  const tier = visaTier(openJobs);
  if (tier === "evergreen" || openJobs === undefined) {
    return {
      tier,
      live: false,
      title: post.title,
      h1: post.title,
      description: visaMetaDescription(post),
      updatedAt: post.updatedAt,
    };
  }
  const jobs = Math.floor(openJobs);
  const n = jobs.toLocaleString("en-US");
  const c = String(post.companies.length);
  const monthStart = `${now.toISOString().slice(0, 7)}-01`;
  return {
    tier,
    live: true,
    openJobs: jobs,
    title: liveTitle(post, tier, n, c),
    h1: `Software Engineer & IT Jobs in ${post.countryName} with Visa Sponsorship (${monthYear(now)})`,
    description: liveDescription(post, tier, n, c),
    updatedAt: monthStart > post.updatedAt ? monthStart : post.updatedAt,
  };
}

export function visaFaq(post: VisaPost) {
  return post.faq.map(({ question, answer }) => ({
    question,
    answer: answer.split("{processingTime}").join(post.processingTime.text),
  }));
}

export function visaArticleSchema(
  post: VisaPost,
  view: VisaPageView = visaPageView(post, undefined),
) {
  const url = visaPostUrl(post);
  const [a, b, c] = post.companies.map((company) => company.name);
  return {
    "@type": "Article",
    headline: view.h1,
    description: `${post.companies.length} companies in ${post.countryName} that sponsor ${post.visaName} for software engineers, including ${a}, ${b}, and ${c}. 2026 salary thresholds and how to apply.`,
    url,
    datePublished: `${post.publishedAt}T00:00:00Z`,
    dateModified: `${view.updatedAt}T00:00:00Z`,
    image: `${BASE_URL}${post.heroImage}`,
    author: {
      "@type": "Person",
      name: "Felix Gerschau",
      url: `${BASE_URL}/about`,
      sameAs: `${BASE_URL}/about`,
    },
    publisher: {
      "@type": "Organization",
      name: "NextLevelJobs.eu",
      url: BASE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${BASE_URL}/android-icon-192x192.png`,
        width: 192,
        height: 192,
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
}

export function visaStructuredData(
  post: VisaPost,
  view: VisaPageView = visaPageView(post, undefined),
) {
  const url = visaPostUrl(post);
  return {
    "@context": "https://schema.org",
    "@graph": [
      visaArticleSchema(post, view),
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: `${BASE_URL}/blog`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: `Visa Sponsorship in ${post.countryName}`,
            item: url,
          },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: visaFaq(post).map(({ question, answer }) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      },
    ],
  };
}

/**
 * Sitemap entries for the visa posts. lastmod is the post's updatedAt, or the
 * first of the current month for posts showing the live count (pass the open
 * jobs per country, as the page does, so both agree).
 */
export const visaSitemapEntries = (
  openJobsByCountry: Record<string, number | undefined> = {},
  now: Date = new Date(),
) =>
  Object.values(visaPosts).map((post) => ({
    slug: `visa-sponsorship/${post.country}`,
    lastmod: visaPageView(post, openJobsByCountry[post.country], now).updatedAt,
  }));
